/**
 * BpmMetrics.jsx — Workspace de Métricas BPM (analítica / process mining).
 * Consume /engine-users/metrics/* (ver xflow_backv2/docs/bpm-metrics.md).
 *
 * Estructura: barra de filtros (rango de fechas) + tabs:
 *   Resumen · Operación (backlog+workload) · Rendimiento (cycle+throughput)
 *   · Análisis (bottlenecks+transitions+variables).
 *
 * Se monta vía /manage/metrics (interceptado en ListData) o ruta /metrics.
 */
import React from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  RefreshCw, Activity, Inbox, AlertTriangle, Clock,
  Users, GitBranch, Layers,
} from "lucide-react";

const hasMenuCode = (items = [], code) => {
  for (const item of items) {
    if (item.code === code) return true;
    if (item.items && hasMenuCode(item.items, code)) return true;
  }
  return false;
};
import {
  ChartComponent, SeriesCollectionDirective, SeriesDirective,
  Inject, LineSeries, ColumnSeries, BarSeries,
  Category, DateTime, Tooltip, Legend, DataLabel,
} from "@syncfusion/ej2-react-charts";
import {
  getBpmOverview, getBpmBacklog, getBpmFlowCycle, getBpmThroughput,
  getBpmBottlenecks, getBpmWorkload, getBpmTransitions, getBpmVariables,
  minutesToHuman, defaultRange,
} from "../../utils/bpmMetrics";

const num = (v) => (v == null || isNaN(Number(v)) ? 0 : Number(v));
const sfTheme = () =>
  document.documentElement.getAttribute("data-theme") === "dark"
    ? "MaterialDark"
    : "Material";

const DATE_PRESETS = [
  { label: "7 días", days: 7 },
  { label: "30 días", days: 30 },
  { label: "90 días", days: 90 },
];

const TABS = [
  { key: "resumen",    label: "Resumen" },
  { key: "operacion", label: "Operación" },
  { key: "rendimiento", label: "Rendimiento" },
  { key: "analisis",  label: "Análisis" },
];

/* ── Tabla genérica: deduce columnas de las keys del primer registro ── */
function MetricTable({ title, rows, hint }) {
  const data = Array.isArray(rows) ? rows : [];
  const cols = data.length ? Object.keys(data[0]) : [];
  return (
    <section className="bpm-card">
      <div className="bpm-card-head">
        <h3>{title}</h3>
        {hint && <span className="bpm-card-sub">{hint}</span>}
        <span className="bpm-card-count">{data.length}</span>
      </div>
      {data.length === 0 ? (
        <p className="bpm-empty">Sin datos en el rango seleccionado.</p>
      ) : (
        <div className="bpm-table-wrap">
          <table className="bpm-table">
            <thead>
              <tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {data.slice(0, 200).map((r, i) => (
                <tr key={i}>
                  {cols.map((c) => (
                    <td key={c}>{r[c] == null ? "—" : String(r[c])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ── Throughput: flujos iniciados vs finalizados por día (líneas) ── */
function ThroughputChart({ flows, tasks }) {
  const flowData = React.useMemo(() => {
    if (!Array.isArray(flows) || flows.length === 0) return [];
    const map = {};
    flows.forEach((r) => {
      const d = r.day;
      if (!map[d]) map[d] = { day: d, date: new Date(d), started: 0, finished: 0 };
      map[d].started  += num(r.started_flows);
      map[d].finished += num(r.finished_flows);
    });
    return Object.values(map).sort((a, b) => a.day.localeCompare(b.day));
  }, [flows]);

  const taskData = React.useMemo(() => {
    if (!Array.isArray(tasks) || tasks.length === 0) return [];
    const map = {};
    tasks.forEach((r) => {
      const d = r.day;
      if (!map[d]) map[d] = { day: d, date: new Date(d), started: 0, finished: 0 };
      map[d].started  += num(r.started_tasks);
      map[d].finished += num(r.finished_tasks);
    });
    return Object.values(map).sort((a, b) => a.day.localeCompare(b.day));
  }, [tasks]);

  const interval = Math.max(1, Math.floor(flowData.length / 8));

  if (flowData.length === 0 && taskData.length === 0) {
    return <p className="bpm-empty">Sin datos de throughput en el rango.</p>;
  }

  return (
    <div className="bpm-grid2">
      {flowData.length > 0 && (
        <section className="bpm-card">
          <div className="bpm-card-head"><GitBranch size={16} /><h3>Flujos por día</h3><span className="bpm-card-sub">iniciados vs finalizados</span></div>
          <div className="bpm-chart-wrap">
            <ChartComponent theme={sfTheme()} height="220"
              tooltip={{ enable: true, shared: true }}
              legendSettings={{ visible: true, position: "Bottom" }}
              primaryXAxis={{ valueType: "DateTime", labelFormat: "dd/MM", intervalType: "Days", interval }}
              primaryYAxis={{ minimum: 0, title: "Flujos" }}>
              <Inject services={[LineSeries, DateTime, Tooltip, Legend]} />
              <SeriesCollectionDirective>
                <SeriesDirective dataSource={flowData} xName="date" yName="started"  name="Iniciados"   type="Line" marker={{ visible: true, width: 5, height: 5 }} />
                <SeriesDirective dataSource={flowData} xName="date" yName="finished" name="Finalizados" type="Line" marker={{ visible: true, width: 5, height: 5 }} />
              </SeriesCollectionDirective>
            </ChartComponent>
          </div>
        </section>
      )}
      {taskData.length > 0 && (
        <section className="bpm-card">
          <div className="bpm-card-head"><Layers size={16} /><h3>Tareas por día</h3><span className="bpm-card-sub">iniciadas vs completadas</span></div>
          <div className="bpm-chart-wrap">
            <ChartComponent theme={sfTheme()} height="220"
              tooltip={{ enable: true, shared: true }}
              legendSettings={{ visible: true, position: "Bottom" }}
              primaryXAxis={{ valueType: "DateTime", labelFormat: "dd/MM", intervalType: "Days", interval }}
              primaryYAxis={{ minimum: 0, title: "Tareas" }}>
              <Inject services={[LineSeries, DateTime, Tooltip, Legend]} />
              <SeriesCollectionDirective>
                <SeriesDirective dataSource={taskData} xName="date" yName="started"  name="Iniciadas"   type="Line" marker={{ visible: true, width: 5, height: 5 }} />
                <SeriesDirective dataSource={taskData} xName="date" yName="finished" name="Completadas" type="Line" marker={{ visible: true, width: 5, height: 5 }} />
              </SeriesCollectionDirective>
            </ChartComponent>
          </div>
        </section>
      )}
    </div>
  );
}

/* ── Backlog: top flujos por tareas activas (columnas) ── */
function BacklogChart({ rows }) {
  const chartData = React.useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const map = {};
    rows.forEach((r) => {
      const name = r.FLOW || r.flow || "—";
      if (!map[name]) map[name] = { flow: name, count: 0, overdue: 0 };
      map[name].count += num(r.count);
      if (r.sla_status === "OVERDUE") map[name].overdue += num(r.count);
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 12);
  }, [rows]);

  if (chartData.length === 0) return null;

  return (
    <section className="bpm-card">
      <div className="bpm-card-head"><Inbox size={16} /><h3>Backlog por flujo</h3><span className="bpm-card-sub">tareas activas (top {chartData.length})</span></div>
      <div className="bpm-chart-wrap">
        <ChartComponent theme={sfTheme()} height="240"
          tooltip={{ enable: true }}
          legendSettings={{ visible: true, position: "Bottom" }}
          primaryXAxis={{ valueType: "Category", labelIntersectAction: "Rotate45", maximumLabelWidth: 80 }}
          primaryYAxis={{ minimum: 0, title: "Tareas" }}>
          <Inject services={[ColumnSeries, Category, Tooltip, Legend, DataLabel]} />
          <SeriesCollectionDirective>
            <SeriesDirective dataSource={chartData} xName="flow" yName="count"   name="Activas"  type="Column" />
            <SeriesDirective dataSource={chartData} xName="flow" yName="overdue" name="Vencidas" type="Column" />
          </SeriesCollectionDirective>
        </ChartComponent>
      </div>
    </section>
  );
}

/* ── Ciclo de flujo: tiempo promedio por flujo (barras horizontales) ── */
function FlowCycleChart({ rows }) {
  const chartData = React.useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const map = {};
    rows.forEach((r) => {
      const name = r.FLOW || r.flow || "—";
      if (!map[name]) map[name] = { flow: name, total: 0, n: 0 };
      map[name].total += num(r.avg_cycle_minutes);
      map[name].n     += 1;
    });
    return Object.values(map)
      .map((r) => ({ flow: r.flow, avgMin: r.n ? Math.round(r.total / r.n) : 0 }))
      .sort((a, b) => b.avgMin - a.avgMin)
      .slice(0, 10);
  }, [rows]);

  if (chartData.length === 0) return null;

  return (
    <section className="bpm-card">
      <div className="bpm-card-head"><Clock size={16} /><h3>Ciclo promedio por flujo</h3><span className="bpm-card-sub">minutos (flujos finalizados)</span></div>
      <div className="bpm-chart-wrap">
        <ChartComponent theme={sfTheme()} height={Math.max(200, chartData.length * 36) + ""}
          tooltip={{ enable: true, format: "${point.x}: <b>${point.y} min</b>" }}
          legendSettings={{ visible: false }}
          primaryXAxis={{ valueType: "Category", title: "" }}
          primaryYAxis={{ minimum: 0, title: "Minutos" }}>
          <Inject services={[BarSeries, Category, Tooltip]} />
          <SeriesCollectionDirective>
            <SeriesDirective dataSource={chartData} xName="flow" yName="avgMin" name="Ciclo promedio" type="Bar" />
          </SeriesCollectionDirective>
        </ChartComponent>
      </div>
    </section>
  );
}

/* ── Bottlenecks: tareas activas acumuladas (barras horizontales) ── */
function BottleneckChart({ rows }) {
  const chartData = React.useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    return [...rows]
      .sort((a, b) => num(b.active_count || b.count) - num(a.active_count || a.count))
      .slice(0, 10)
      .map((r) => ({
        task:  r.TASK || r.task_code || String(r.TASKID || "—"),
        count: num(r.active_count || r.count),
        avgAge: num(r.avg_age_minutes),
      }));
  }, [rows]);

  if (chartData.length === 0) return null;

  return (
    <section className="bpm-card">
      <div className="bpm-card-head"><AlertTriangle size={16} /><h3>Cuellos de botella</h3><span className="bpm-card-sub">tareas activas acumuladas</span></div>
      <div className="bpm-chart-wrap">
        <ChartComponent theme={sfTheme()} height={Math.max(200, chartData.length * 36) + ""}
          tooltip={{ enable: true, format: "${point.x}: <b>${point.y} tareas</b>" }}
          legendSettings={{ visible: false }}
          primaryXAxis={{ valueType: "Category" }}
          primaryYAxis={{ minimum: 0, title: "Tareas activas" }}>
          <Inject services={[BarSeries, Category, Tooltip]} />
          <SeriesCollectionDirective>
            <SeriesDirective dataSource={chartData} xName="task" yName="count" name="Activas" type="Bar" />
          </SeriesCollectionDirective>
        </ChartComponent>
      </div>
    </section>
  );
}

/* ── Workload: tareas activas por actor (columnas) ── */
function WorkloadChart({ rows }) {
  const chartData = React.useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const map = {};
    rows.forEach((r) => {
      const name = r.ACTOR || r.actor || r.USERNAME || "—";
      if (!map[name]) map[name] = { actor: name, active: 0, unassigned: 0 };
      map[name].active     += num(r.active_tasks);
      map[name].unassigned += num(r.unassigned_tasks);
    });
    return Object.values(map).sort((a, b) => b.active - a.active).slice(0, 12);
  }, [rows]);

  if (chartData.length === 0) return null;

  return (
    <section className="bpm-card">
      <div className="bpm-card-head"><Users size={16} /><h3>Carga por actor</h3><span className="bpm-card-sub">tareas activas (top {chartData.length})</span></div>
      <div className="bpm-chart-wrap">
        <ChartComponent theme={sfTheme()} height="240"
          tooltip={{ enable: true }}
          legendSettings={{ visible: true, position: "Bottom" }}
          primaryXAxis={{ valueType: "Category", labelIntersectAction: "Rotate45", maximumLabelWidth: 80 }}
          primaryYAxis={{ minimum: 0, title: "Tareas" }}>
          <Inject services={[ColumnSeries, Category, Tooltip, Legend]} />
          <SeriesCollectionDirective>
            <SeriesDirective dataSource={chartData} xName="actor" yName="active"     name="Activas"      type="Column" />
            <SeriesDirective dataSource={chartData} xName="actor" yName="unassigned" name="Sin asignar"  type="Column" />
          </SeriesCollectionDirective>
        </ChartComponent>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */

const BpmMetrics = () => {
  const { defConfig, defLang, username, menu } = useSelector((s) => s.Login);

  const hasAccess = React.useMemo(() => hasMenuCode(menu || [], "metrics"), [menu]);
  if (!hasAccess) return <Navigate to="/" replace />;

  const [preset,     setPreset]     = React.useState(1); // 30 días
  const [range,      setRange]      = React.useState(defaultRange(30));
  const [tab,        setTab]        = React.useState("resumen");
  const [refreshing, setRefreshing] = React.useState(false);

  const [overview, setOverview] = React.useState(null);
  const [loaded,   setLoaded]   = React.useState({});
  const [data,     setData]     = React.useState({});
  const [loading,  setLoading]  = React.useState(false);

  const filters = React.useMemo(() => ({ ...range }), [range]);

  const applyPreset = (i, days) => {
    setPreset(i);
    setRange(defaultRange(days));
    setLoaded({});
  };

  const loadOverview = React.useCallback(async () => {
    setLoading(true);
    const o = await getBpmOverview(filters);
    setOverview(o && o.status ? o : { status: false });
    setLoading(false);
  }, [filters]);

  const loadTab = React.useCallback(async (key) => {
    if (loaded[key]) return;
    setLoading(true);
    let next = {};
    if (key === "operacion") {
      next.backlog   = await getBpmBacklog(filters);
      next.workload  = await getBpmWorkload(filters);
    } else if (key === "rendimiento") {
      next.flowCycle  = await getBpmFlowCycle(filters);
      next.throughput = await getBpmThroughput(filters);
    } else if (key === "analisis") {
      next.bottlenecks = await getBpmBottlenecks(filters);
      next.transitions = await getBpmTransitions(filters);
      next.variables   = await getBpmVariables(filters);
    }
    setData((d) => ({ ...d, ...next }));
    setLoaded((l) => ({ ...l, [key]: true }));
    setLoading(false);
  }, [filters, loaded]);

  React.useEffect(() => {
    loadOverview();
    if (tab !== "resumen") loadTab(tab);
  }, [range]); // intentional: only re-fetch on range change

  React.useEffect(() => {
    if (tab === "resumen") return;
    loadTab(tab);
  }, [tab]); // intentional: only re-fetch on tab change

  const refresh = () => {
    setRefreshing(true);
    setLoaded({});
    loadOverview();
    if (tab !== "resumen") loadTab(tab);
    setTimeout(() => setRefreshing(false), 800);
  };

  const sum         = overview?.summary        || {};
  const health      = overview?.activeTaskHealth || {};
  const activeTasks = num(health.active_tasks);
  const overdue     = num(health.overdue_tasks);
  const atRisk      = num(health.at_risk_tasks);
  const onTime      = Math.max(0, activeTasks - overdue - atRisk);
  const pct         = (n) => (activeTasks > 0 ? (n / activeTasks) * 100 : 0);

  const KPIS = [
    { k: "af", label: "Flujos activos",     value: num(sum.active_flows),        icon: <Activity size={20} />,      cls: "bpm-kpi--primary" },
    { k: "ff", label: "Flujos finalizados", value: num(sum.finished_flows),       icon: <GitBranch size={20} />,     cls: "bpm-kpi--teal"   },
    { k: "at", label: "Tareas activas",     value: activeTasks,                  icon: <Inbox size={20} />,         cls: "bpm-kpi--violet" },
    { k: "ov", label: "Vencidas",           value: overdue,                      icon: <AlertTriangle size={20} />, cls: "bpm-kpi--danger" },
    { k: "ar", label: "En riesgo",          value: atRisk,                       icon: <Clock size={20} />,         cls: "bpm-kpi--amber"  },
    { k: "un", label: "Sin asignar",        value: num(health.unassigned_tasks), icon: <Users size={20} />,         cls: "bpm-kpi--slate"  },
  ];

  /* helpers para extraer arrays de shapes variables */
  const arr = (v) => (Array.isArray(v) ? v : v?.result || v?.rows || []);

  return (
    <div className="bpm-page">
      {/* ── Header + filtros ── */}
      <div className="bpm-hero">
        <div>
          <span className="xflow-kicker">Métricas BPM</span>
          <h1>Analítica de procesos</h1>
          <p>Hola, {username} — salud operativa y de procesos</p>
        </div>
        <div className="bpm-hero-controls">
          <div className="bpm-presets">
            {DATE_PRESETS.map((p, i) => (
              <button key={i} type="button"
                className={"bpm-preset" + (preset === i ? " bpm-preset--active" : "")}
                onClick={() => applyPreset(i, p.days)}>
                {p.label}
              </button>
            ))}
          </div>
          <button
            className={"e-btn e-primary xflow-dashboard-refresh" + (refreshing ? " is-refreshing" : "")}
            onClick={refresh}>
            <RefreshCw size={16} className="xdash-refresh-icon" />
            {defConfig?.[defLang]?.refreshlabel || "Actualizar"}
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="bpm-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab"
            aria-selected={tab === t.key}
            className={"bpm-tab" + (tab === t.key ? " bpm-tab--active" : "")}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="bpm-loading">Cargando métricas…</div>}

      {/* ══ RESUMEN ══ */}
      {tab === "resumen" && (
        <>
          {overview && overview.status === false ? (
            <div className="bpm-empty bpm-empty--block">No se pudieron cargar las métricas.</div>
          ) : (
            <>
              <div className="bpm-kpis">
                {KPIS.map((k) => (
                  <div key={k.k} className={"bpm-kpi " + k.cls}>
                    <div className="bpm-kpi-icon">{k.icon}</div>
                    <div className="bpm-kpi-meta">
                      <span className="bpm-kpi-value">{k.value}</span>
                      <span className="bpm-kpi-label">{k.label}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bpm-grid2">
                <section className="bpm-card">
                  <div className="bpm-card-head"><Clock size={16} /><h3>Salud SLA (tareas activas)</h3></div>
                  <div className="bpm-sla-bar">
                    <span className="bpm-sla-seg bpm-sla--ontime" style={{ width: pct(onTime) + "%" }} title={"En tiempo: " + onTime} />
                    <span className="bpm-sla-seg bpm-sla--risk"   style={{ width: pct(atRisk)  + "%" }} title={"En riesgo: "  + atRisk} />
                    <span className="bpm-sla-seg bpm-sla--over"   style={{ width: pct(overdue) + "%" }} title={"Vencidas: "   + overdue} />
                  </div>
                  <div className="bpm-sla-legend">
                    <span><i className="bpm-dot bpm-dot--ontime" />En tiempo <b>{onTime}</b></span>
                    <span><i className="bpm-dot bpm-dot--risk"   />En riesgo <b>{atRisk}</b></span>
                    <span><i className="bpm-dot bpm-dot--over"   />Vencidas  <b>{overdue}</b></span>
                  </div>
                </section>

                <section className="bpm-card">
                  <div className="bpm-card-head"><Layers size={16} /><h3>Ciclo</h3></div>
                  <div className="bpm-stat-row">
                    <div className="bpm-stat">
                      <span className="bpm-stat-value">{minutesToHuman(sum.avg_cycle_minutes)}</span>
                      <span className="bpm-stat-label">Ciclo promedio</span>
                    </div>
                    <div className="bpm-stat">
                      <span className="bpm-stat-value">{minutesToHuman(sum.max_active_age_minutes)}</span>
                      <span className="bpm-stat-label">Tarea activa más antigua</span>
                    </div>
                    <div className="bpm-stat">
                      <span className="bpm-stat-value">{num(sum.total_flows)}</span>
                      <span className="bpm-stat-label">Flujos totales</span>
                    </div>
                  </div>
                </section>
              </div>

              <MetricTable title="Top flujos" rows={overview?.topFlows} hint="por actividad" />
            </>
          )}
        </>
      )}

      {/* ══ OPERACIÓN ══ */}
      {tab === "operacion" && (
        <>
          <BacklogChart rows={arr(data.backlog)} />
          <MetricTable title="Backlog detalle" rows={arr(data.backlog)} hint="activas por flujo / tarea / oficina / SLA" />
          <WorkloadChart rows={arr(data.workload)} />
          <MetricTable title="Carga de trabajo" rows={arr(data.workload)} hint="por oficina / actor / usuario" />
        </>
      )}

      {/* ══ RENDIMIENTO ══ */}
      {tab === "rendimiento" && (
        <>
          <ThroughputChart flows={data.throughput?.flows} tasks={data.throughput?.tasks} />
          <MetricTable title="Throughput — flujos por día"  rows={data.throughput?.flows} hint="iniciados vs finalizados" />
          <MetricTable title="Throughput — tareas por día"  rows={data.throughput?.tasks} />
          <FlowCycleChart rows={arr(data.flowCycle)} />
          <MetricTable title="Tiempo de ciclo por flujo"   rows={arr(data.flowCycle)} hint="flujos finalizados" />
        </>
      )}

      {/* ══ ANÁLISIS ══ */}
      {tab === "analisis" && (
        <>
          <BottleneckChart rows={data.bottlenecks?.activeBottlenecks} />
          <MetricTable title="Cuellos de botella (activos)"    rows={data.bottlenecks?.activeBottlenecks} hint="acumulación de tareas activas" />
          <MetricTable title="Tareas lentas (completadas)"     rows={data.bottlenecks?.slowCompletedTasks} />
          <MetricTable title="Opciones más usadas"             rows={data.transitions?.options} />
          <MetricTable title="Transiciones"                    rows={data.transitions?.transitions} hint="origen → destino" />
          <MetricTable title="Variables de memoria"            rows={arr(data.variables)} hint="uso y cardinalidad" />
        </>
      )}
    </div>
  );
};

export default BpmMetrics;
